// src/pages/ReviewerDownloadTemplates.jsx
import { useEffect, useState, useCallback } from "react";
import ReviewerSidebar from "../components/ReviewerSidebar";
import api from "../services/api";

export default function ReviewerDownloadTemplates() {
  const [esmps, setEsmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // Track which file is being downloaded
  const [filterStatus, setFilterStatus] = useState("all"); // Filter by status

  const token = localStorage.getItem("token");

  // Fetch submitted ESMPs (wrapped in useCallback so it's safe in useEffect)
  const fetchEsmps = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/esmp/my-esmps", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEsmps(res.data.data);
    } catch (err) {
      console.error("Failed to load ESMPs", err);
      alert("Failed to load ESMPs. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEsmps();
  }, [fetchEsmps]);

  // Download file handler
  const downloadFile = async (esmp) => {
    try {
      setDownloading(esmp.id);

      if (!token) {
        alert("Session expired. Please login again.");
        window.location.href = "/";
        return;
      }

      console.log("Downloading ESMP:", esmp.id); // Debug log

      const res = await api.get(`/reviewer/esmps/${esmp.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      console.log("Download response:", res); // Debug log

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", esmp.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download file", err);

      if (err.response?.status === 401) {
        alert("Session expired. Please login again.");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/";
      } else {
        alert(`Failed to download file: ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setDownloading(null);
    }
  };

  // Filter ESMPs by status
  const filteredEsmps = esmps.filter((esmp) => {
    if (filterStatus === "all") return true;
    return esmp.status === filterStatus;
  });

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ReviewerSidebar />

      <main className="flex-1 p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold">Download ESMP Files</h2>
          <button
            onClick={fetchEsmps}
            className="bg-white text-green-700 px-4 py-2 rounded-lg hover:bg-gray-100 font-semibold"
          >
            Refresh
          </button>
        </div>

        {/* Filter Section */}
        <div className="bg-white p-4 rounded-xl shadow flex items-center gap-4">
          <label className="font-semibold text-gray-700">Filter by Status:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2"
          >
            <option value="all">All</option>
            <option value="Submitted">Submitted</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Returned">Returned</option>
          </select>
          <span className="text-gray-500 text-sm ml-auto">{filteredEsmps.length} file(s) found</span>
        </div>

        {/* Files List */}
        <div className="bg-white p-6 rounded-xl shadow">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading ESMPs...</p>
            </div>
          ) : filteredEsmps.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No ESMPs available to download.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-green-50 text-green-700">
                    <th className="p-3 text-left">ESMP ID</th>
                    <th className="p-3 text-left">Project Name</th>
                    <th className="p-3 text-left">District</th>
                    <th className="p-3 text-left">Submitted By</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">File Name</th>
                    <th className="p-3 text-left">Submitted Date</th>
                    <th className="p-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEsmps.map((esmp) => (
                    <tr key={esmp.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">{esmp.esmp_id}</td>
                      <td className="p-3">{esmp.project_name}</td>
                      <td className="p-3">{esmp.district}</td>
                      <td className="p-3">{esmp.User ? esmp.User.name : "N/A"}</td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            esmp.status === "Approved"
                              ? "bg-green-100 text-green-700"
                              : esmp.status === "Returned"
                              ? "bg-yellow-100 text-yellow-700"
                              : esmp.status === "Pending"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {esmp.status}
                        </span>
                      </td>
                      <td className="p-3 truncate max-w-xs" title={esmp.file_name}>
                        {esmp.file_name}
                      </td>
                      <td className="p-3 text-sm text-gray-500">
                        {new Date(esmp.createdAt).toLocaleDateString()}
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() => downloadFile(esmp)}
                          disabled={downloading === esmp.id}
                          className={`px-4 py-2 rounded-lg font-semibold transition ${
                            downloading === esmp.id
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-green-600 hover:bg-green-700 text-white"
                          }`}
                        >
                          {downloading === esmp.id ? "Downloading..." : "Download"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-blue-500">
            <p className="text-xs text-gray-500">Total ESMPs</p>
            <h3 className="text-2xl font-bold text-blue-600">{esmps.length}</h3>
          </div>
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-yellow-500">
            <p className="text-xs text-gray-500">Submitted</p>
            <h3 className="text-2xl font-bold text-yellow-600">
              {esmps.filter((e) => e.status === "Submitted").length}
            </h3>
          </div>
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-orange-500">
            <p className="text-xs text-gray-500">Pending</p>
            <h3 className="text-2xl font-bold text-orange-600">
              {esmps.filter((e) => e.status === "Pending").length}
            </h3>
          </div>
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-green-500">
            <p className="text-xs text-gray-500">Approved</p>
            <h3 className="text-2xl font-bold text-green-600">
              {esmps.filter((e) => e.status === "Approved").length}
            </h3>
          </div>
        </div>
      </main>
    </div>
  );
}

